import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { CustomerShippingAddress } from '../../modules/backend/openapi/api-types';
import { RadioGroup } from '../radio-group/radio-group';
import { LinkButton } from '../link/link';
import { notNil } from '../../core/helpers/is-nil';
import { DefinitionBasic } from '../definition-list/definition-renderers/definition-basic/definition-basic';
import { DialogLayoutButton } from '../dialog/dialog-layout-button';

type Props = {
  addresses: CustomerShippingAddress[];
  readOnly?: boolean;
  selectedDeliveryAddress: CustomerShippingAddress | null;
  onDialogConfirm: (addressId: string) => void;
};

export const DeliveryAddressDialog: React.FC<Props> = ({
  addresses,
  readOnly = false,
  selectedDeliveryAddress,
  onDialogConfirm,
}) => {
  const { t } = useTranslation();
  const [addressId, setAddressId] = useState<string>(selectedDeliveryAddress?.addressId ?? '');
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const wrappedHandleClick = () => {
    onDialogConfirm(addressId);
    handleClose();
  };

  const options = addresses.map((address) => ({
    label: address.addressString,
    value: address.addressId,
  }));

  const canEdit = addresses.length > 1 && !readOnly;

  return (
    <DefinitionBasic
      term={t('global.deliveryAddress')}
      action={
        canEdit ? (
          <Dialog
            isOpen={open}
            title={t('global.deliveryAddress.change')}
            trigger={
              <LinkButton
                aria-label={t('global.deliveryAddress.change')}
                icon="pen-box"
                onClick={() => setOpen(!open)}
              />
            }
            onOpenChange={setOpen}
          >
            <DialogLayoutButton
              buttons={
                <>
                  <Button variant="secondary" onClick={handleClose}>
                    {t('global.cancel')}
                  </Button>
                  <Button onClick={wrappedHandleClick}>{t('global.confirm')}</Button>
                </>
              }
            >
              <RadioGroup
                defaultValue={addressId}
                label={t('global.deliveryAddress')}
                options={options}
                onChange={(addressId) => setAddressId(addressId)}
              />
            </DialogLayoutButton>
          </Dialog>
        ) : null
      }
    >
      {notNil(selectedDeliveryAddress) ? selectedDeliveryAddress.addressString : null}
    </DefinitionBasic>
  );
};
